import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for listening to Bluetooth barcode scanner input.
 * Most Bluetooth barcode scanners work in HID mode (keyboard wedge).
 * They type the barcode data rapidly followed by an Enter key.
 * This hook detects rapid input sequences ending with Enter.
 */
export function useBarcodeScanner({ onScan, enabled = true }) {
    const [lastScan, setLastScan] = useState('');
    const bufferRef = useRef('');
    const timerRef = useRef(null);

    const handleKeyDown = useCallback((e) => {
        if (!enabled) return;

        // Ignore if user is typing in an input field (unless it's the scanner field itself)
        const tag = e.target?.tagName?.toLowerCase();
        const isInputField = tag === 'input' || tag === 'textarea';
        const isScannerField = e.target?.dataset?.scannerInput === 'true';
        if (isInputField && !isScannerField) return;

        // Barcode scanners type very fast (< 50ms between chars) and end with Enter
        if (e.key === 'Enter' && bufferRef.current.length >= 3) {
            e.preventDefault();
            const scannedValue = bufferRef.current.trim();
            setLastScan(scannedValue);
            onScan?.(scannedValue);
            bufferRef.current = '';
            return;
        }

        // Only accept printable characters
        if (e.key.length === 1) {
            bufferRef.current += e.key;

            // Clear buffer after 100ms of no input (human typing is slower)
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                bufferRef.current = '';
            }, 100);
        }
    }, [enabled, onScan]);

    useEffect(() => {
        if (enabled) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            clearTimeout(timerRef.current);
        };
    }, [enabled, handleKeyDown]);

    const clearLastScan = useCallback(() => setLastScan(''), []);

    return { lastScan, clearLastScan };
}
